<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import moment from 'moment';
import CustomLink from '@/Components/CustomLink.vue';
import Button from '@/Components/Button.vue';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  isInnerPage: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['focus-license-input']);

// Define scroll-based animation parameters
const scrollPosition = ref(0);
const scrollThreshold = 10; // Minimum scroll to start animations
const maxScrollEffect = 150; // Scroll position at which animations are complete

// Calculate animation values based on scroll position
const orangeBgOpacity = computed(() => {
    // If it's an inner page, always return 1 (fully visible)
    if (props.isInnerPage) return 1;
    
    if (scrollPosition.value <= scrollThreshold) return 0;
    const progress = Math.min(1, (scrollPosition.value - scrollThreshold) / maxScrollEffect);
    return progress; // Linear interpolation between 0 and 1
});

const whiteBgProgress = computed(() => {
    // If it's an inner page, always return 1 (fully visible)
    if (props.isInnerPage) return 1;
    
    if (scrollPosition.value <= scrollThreshold + 20) return 0; // Add a small delay for white bg
    const progress = Math.min(1, (scrollPosition.value - scrollThreshold - 20) / maxScrollEffect);
    return progress; // Linear interpolation between 0 and 1
});

// Transform values based on scroll
const orangeBgTransform = computed(() => {
    // If it's an inner page, show the final position
    if (props.isInnerPage) return 'translateX(0%)';
    
    // Start from right (100%) and move to position (0%)
    const translateX = 100 - (orangeBgOpacity.value * 100);
    return `translateX(${translateX}%)`;
});

const whiteBgTransform = computed(() => {
    // If it's an inner page, show the final position
    if (props.isInnerPage) return 'translateY(-50%)';
    
    // Start from top (-100%) and move to position (-50%)
    const translateY = -100 + (whiteBgProgress.value * 50);
    return `translateY(${translateY}%)`;
});

// Add a new computed property specifically for text color control
const shouldUseDeepBlueText = computed(() => {
  // On inner pages, we never use deep blue text in the header
  if (props.isInnerPage) return false;
  
  // On the home page, check if we've scrolled enough to show the white background
  // Using a threshold of 0.3 to ensure the background is visible enough before changing text color
  return scrollPosition.value > (scrollThreshold + 30) && whiteBgProgress.value > 0.3;
});

function handleScroll() {
    // Only track scroll position if not an inner page
    if (!props.isInnerPage) {
        scrollPosition.value = window.scrollY;
    }
}

// Add event listeners when component mounts
onMounted(() => {
    if (!props.isInnerPage) {
        window.addEventListener('scroll', handleScroll);
        // Initialize scroll state
        handleScroll();
    }
});

// Clean up event listeners when component unmounts
onBeforeUnmount(() => {
    if (!props.isInnerPage) {
        window.removeEventListener('scroll', handleScroll);
    }
});

const openingTimes = [
    {
        day: 1,
        open: '09:00',
        close: '20:00'
    },
    {
        day: 2,
        open: '09:00',
        close: '20:00'
    },
    {
        day: 3,
        open: '09:00',
        close: '20:00'
    },
    {
        day: 4,
        open: '09:00',
        close: '20:00'
    },
    {
        day: 5,
        open: '09:00',
        close: '20:00'
    },
    {
        day: 6,
        open: '09:00',
        close: '17:00'
    }
];

function isOpen() {
    const now = moment();
    const day = now.day();
    const time = now.format('HH:mm');

    const openingTime = openingTimes.find(time => time.day === day);
    if (!openingTime) {
        return false;
    }

    return time >= openingTime.open && time <= openingTime.close;
}

function openTime() {
    //get the day and get the closing time for this day
    const now = moment();
    const day = now.day();

    const openingTime = openingTimes.find(time => time.day === day);

    if (!openingTime) {
        return 'maandag 09:00';
    }

    return openingTime.open;
}

function closeTime() {
    //get the day and get the closing time for this day
    const now = moment();
    const day = now.day();

    const openingTime = openingTimes.find(time => time.day === day);

    return openingTime.close;
}

// Function to handle button click - modified to handle different pages
function handleAutoAanmeldenClick() {
  emit('focus-license-input');
  
  // Check if we're on the homepage by examining the current URL
  const isHomepage = window.location.pathname === '/' || window.location.pathname === '/home';
  
  if (isHomepage) {
    // If already on homepage, just scroll to the kenteken section
    const kentekenSection = document.getElementById('kenteken');
    if (kentekenSection) {
      kentekenSection.scrollIntoView({ behavior: 'smooth' });
    }
  } else {
    // If on another page, navigate to the homepage with a hash to trigger scrolling
    window.location.href = '/#kenteken';
  }
}
</script>

<template>

    <div class="fixed w-full top-0 z-40">
        <div class="flex isolate overflow-hidden py-0.5 md:py-1.5 relative">
            <div class="flex w-full max-w-7xl px-4 sm:px-6 2xl:px-0 mx-auto justify-between gap-8 items-center">
                <div class="items-center hidden sm:flex">
                    <div>
                        <div class="flex items-center">
                            <font-awesome-icon 
                                :icon="['fas', 'star']" 
                                v-for="rating in [0, 1, 2, 3]" 
                                :key="rating" 
                                :class="[shouldUseDeepBlueText ? 'text-yellow-500' : 'text-brightorange' , 'h-3.5 w-3.5 shrink-0']" 
                                aria-hidden="true" 
                            />                                
                            <font-awesome-icon 
                                :icon="['fas', 'star-half-stroke']" 
                                :class="[shouldUseDeepBlueText ? 'text-yellow-500' : 'text-brightorange' , 'h-3.5 w-3.5 shrink-0']" 
                                aria-hidden="true" 
                            />
                        </div>
                        <p class="sr-only">Google reviews</p>
                    </div>
                    <p :class="[shouldUseDeepBlueText ? 'text-gray-900' : 'text-white', 'hidden lg:flex flex-row gap-1 ml-2 text-xs sm:text-sm']">4.6 <span class="hidden xl:block"> beoordeling</span> op  <a target="_blank" rel="noopener noreferrer" href="https://www.google.com/search?q=Mijnautosnelweg+Reviews" class="underline font-bold">Google</a></p>
                    <p v-if="shouldUseDeepBlueText" class="flex flex-row lg:hidden gap-1 ml-2 text-gray-900 text-xs sm:text-sm">5 op  <a target="_blank" rel="noopener noreferrer" href="https://www.google.com/search?q=Mijnautosnelweg+Reviews" class="underline font-bold">Google</a></p>
                </div>
                <!-- Improved phone number text color logic using the new computed property -->
                <p class="text-xs sm:text-sm leading-6 mx-auto sm:mx-0" :class="[ shouldUseDeepBlueText ? 'text-deepblue' : 'text-white', 'lg:text-white']">
                    <a :href="isOpen() ? 'tel:+31850477766' : '#'" class="flex flex-row gap-2 items-center">
                        <span class="hidden md:block font-semibold">085 04 777 66</span>
                        <p class="mr-3 mb-3" v-if="isOpen()">
                            <font-awesome-icon :icon="['fas','phone']" class="absolute text-royalblue-500" />
                            <font-awesome-icon :icon="['fas','phone']" :class="[shouldUseDeepBlueText ? 'absolute animate-ping text-royalblue-500' : 'absolute animate-ping text-royalblue-500']" />
                        </p>
                        <p class="mr-3 mb-3" v-else>
                            <font-awesome-icon :icon="['fas','phone']" class="absolute text-red-600" />
                        </p>
                        <span class="hidden sm:block font-semibold" v-if="isOpen()">Wij zijn vandaag bereikbaar tot {{ closeTime() }}</span>
                        <span class="hidden sm:block font-semibold" v-else>Wij zijn weer bereikbaar vanaf {{ openTime() }}</span>
                        <span class="sm:hidden font-semibold" v-if="isOpen()">Wij zijn vandaag Bereikbaar tot {{ closeTime() }}</span>
                        <span class="sm:hidden font-semibold" v-else>Wij zijn weer bereikbaar vanaf {{ openTime() }}</span>
                    </a>
                </p>
            </div>
        </div>
        <Disclosure as="nav" class="relative" v-slot="{ open, close }">
            <div class="relative">
                <div class="mx-auto w-full max-w-7xl px-4 sm:px-6 2xl:px-0 mt-2">
                    <div class="flex w-full justify-between">
                        <div class="-ml-2 mr-2 flex items-center lg:hidden">
                            <DisclosureButton class="relative inline-flex items-center justify-center rounded-md p-2 text-royalblue-500 hover:text-royalblue-500/80 focus:outline-none focus:ring-0">
                                <span class="sr-only">Open main menu</span>
                                <font-awesome-icon :icon="['far', 'bars']" v-if="!open" :class="[shouldUseDeepBlueText ? 'text-deepblue' : 'text-white', 'h-6 w-6 shrink-0']" />
                                <font-awesome-icon :icon="['far', 'xmark']" v-else :class="[shouldUseDeepBlueText ? 'text-deepblue' : 'text-white', 'h-6 w-6 shrink-0']" />
                            </DisclosureButton>
                        </div>
                        <div class="flex flex-shrink-0 items-center">
                            <CustomLink href="/">
                                <picture>
                                    <img :src="props.isInnerPage || shouldUseDeepBlueText ? '/img/logo_full.svg' : '/img/logo_full_white.svg'" 
                                         class="relative h-8 w-auto mt-2 transition-all duration-500" 
                                         alt="mijnautosnelweg" />
                                </picture>
                            </CustomLink>
                        </div>
                        <div class="relative hidden lg:flex gap-8 px-8 w-full justify-center 2xl:justify-end">
                            <CustomLink href="/#hoe" color="white" :animated="true" fontWeight="bold" class="ml-32">
                                Hoe het werkt?
                            </CustomLink>
                            <CustomLink href="/#methode" color="white" :animated="true" fontWeight="bold">
                                Onze methode
                            </CustomLink>
                            <CustomLink href="/#faq" color="white" :animated="true" fontWeight="bold">
                                FAQ
                            </CustomLink>
                            <CustomLink href="/#contact" color="white" :animated="true" fontWeight="bold">
                                Contact
                            </CustomLink>
                            <div class="hidden lg:flex absolute -top-10 lg:left-12 xl:left-28 right-0 h-32 w-screen transform -translate-y-1/2 -z-10 flex-row"
                                :style="{ transform: orangeBgTransform, opacity: orangeBgOpacity }">
                                <img :src="'/img/navbar_vector.svg'" class="h-full object-cover object-left" />
                                <div class="bg-lessbrightorange w-screen h-full"></div>
                            </div>
                        </div>
                        <!-- Update the auto aanmelden button to link to the index hero section -->
                        <Button @click="handleAutoAanmeldenClick" color="secondary" class="hidden sm:block px-4">
                            Auto aanmelden
                        </Button>
                    </div>
                </div>
                <div class="absolute top-0 right-1/2 left-0 h-28 lg:h-32 bg-white w-full -z-20 shadow-xl shadow-deepblue/5" :style="{ transform: whiteBgTransform, opacity: whiteBgProgress }">
                </div>
            </div>
            
            <!-- Redesigned mobile menu -->
            <DisclosurePanel class="lg:hidden absolute top-16 left-2 right-2 bg-white z-30 shadow-xl shadow-deepblue/10 rounded-b-lg overflow-hidden">
                <div class="p-4">
                    <!-- Navigation links -->
                    <div class="space-y-1">
                        <CustomLink @click="close" href="/#hoe" color="secondary" fontWeight="medium" class="flex w-full py-3">
                            Hoe werkt het?
                        </CustomLink>
                        <div class="py-2">
                            <div class="h-px bg-royalblue-500"></div>
                        </div>
                        <CustomLink @click="close" href="/#methode" color="secondary" fontWeight="medium" class="flex w-full py-3">
                            Onze methode
                        </CustomLink>
                        <CustomLink @click="close" href="/#faq" color="secondary" fontWeight="medium" class="flex w-full py-3">
                            FAQ
                        </CustomLink>
                        <CustomLink @click="close" href="/#contact" color="secondary" fontWeight="medium" class="flex w-full py-3">
                            Contact
                        </CustomLink>
                    </div>
                    <div class="py-2">
                        <div class="h-px bg-royalblue-500"></div>
                    </div>
                    <!-- Social media section with CustomLink components -->
                    <div class="space-y-1">
                        <CustomLink href="https://instagram.com/mijnautosnelweg" target="_blank" color="secondary" :useInertia="false" fontWeight="medium" icon="instagram" iconType="fab" class="flex items-center space-x-2 py-3" >
                            <span>Instagram</span>
                        </CustomLink>
                        <CustomLink href="https://facebook.com/mijnautosnelweg" target="_blank" color="secondary" :useInertia="false" fontWeight="medium" icon="facebook" iconType="fab" class="flex items-center space-x-2 py-3" >
                            <span>Facebook</span>
                        </CustomLink>
                    </div>
                    <div class="py-2">
                        <div class="h-px bg-royalblue-500"></div>
                    </div>
                    <!-- Update the mobile button to link to the index hero section -->
                    <Button @click="() => { close(); handleAutoAanmeldenClick(); }" color="primary" class="w-full">
                        Auto aanmelden
                    </Button>
                </div>
            </DisclosurePanel>
        </Disclosure>
    </div>

</template>